import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlowNav } from "../components/FlowNav";
import {
  getQuestionOrder,
  resolveContactNow,
  resolveFromAnswers,
  resolveInfoOnly,
  resolveSkipAll,
  resolveUnsafeToAnswer,
} from "../help/resolveIndividual";
import type { HelpView, IndicatorAnswer, ResolvedRouting } from "../help/types";
import { useSession } from "../state/SessionProvider";

/**
 * Individual / Private Help — navigation and information, not screening.
 * Answers stay in React memory only (SessionProvider). Never in URLs or storage.
 */
export function HelpPage() {
  const navigate = useNavigate();
  const {
    translate,
    helpAnswers,
    setHelpAnswer,
    clearHelpAnswers,
    setResourceCategories,
    setResourceReturnPath,
    helpBookmark,
    setHelpBookmark,
    clearQrContext,
  } = useSession();

  const [view, setView] = useState<HelpView>("hub");
  const [history, setHistory] = useState<HelpView[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [optionsRouting, setOptionsRouting] = useState<ResolvedRouting | null>(
    null,
  );
  const headingRef = useRef<HTMLHeadingElement>(null);
  const restored = useRef(false);

  const questionOrder = getQuestionOrder();

  useEffect(() => {
    if (restored.current || !helpBookmark) return;
    restored.current = true;
    setView(helpBookmark.view as HelpView);
    setQuestionIndex(helpBookmark.questionIndex);
    setHistory(helpBookmark.history as HelpView[]);
    setHelpBookmark(null);
  }, [helpBookmark, setHelpBookmark]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [view, questionIndex]);

  function pushView(next: HelpView) {
    setHistory((h) => [...h, view]);
    setView(next);
  }

  function goBack() {
    if (view === "questions" && questionIndex > 0) {
      setQuestionIndex((i) => i - 1);
      return;
    }
    const prev = history[history.length - 1] ?? "hub";
    setHistory((h) => h.slice(0, -1));
    setView(prev);
  }

  function clearHelpFlow() {
    clearHelpAnswers();
    setOptionsRouting(null);
    setActiveModules([]);
    setHistory([]);
    setQuestionIndex(0);
    setView("hub");
    setHelpBookmark(null);
    setResourceCategories(null);
  }

  /** Mode hub ← Back → / and clears this mode’s transient state + QR context. */
  function goBackToLanding() {
    clearHelpFlow();
    clearQrContext();
    navigate("/");
  }

  /** Nested Home → / and clears this mode’s transient state + QR context. */
  function goHome() {
    clearHelpFlow();
    clearQrContext();
    navigate("/");
  }

  function goTalk(categories: string[]) {
    setHelpBookmark({
      view,
      questionIndex,
      history,
    });
    setResourceReturnPath("/help");
    setResourceCategories(categories);
    navigate("/resources");
  }

  function goOptions(routing: ResolvedRouting) {
    setOptionsRouting(routing);
    pushView("options");
  }

  function openModules(modules: string[]) {
    setActiveModules(modules);
    pushView("modules");
  }

  function startQuestions() {
    setQuestionIndex(0);
    pushView("questions");
  }

  function recordAndAdvance(id: string, value: IndicatorAnswer) {
    setHelpAnswer(id, value);
    const nextAnswers = { ...helpAnswers, [id]: value };
    if (questionIndex >= questionOrder.length - 1) {
      goOptions(resolveFromAnswers(nextAnswers));
      return;
    }
    setQuestionIndex((i) => i + 1);
  }

  function skipAll() {
    clearHelpAnswers();
    goOptions(resolveSkipAll());
  }

  const routing = optionsRouting ?? resolveFromAnswers(helpAnswers);
  const nestedNav = <FlowNav onBack={goBack} onHome={goHome} />;

  if (view === "unsure") {
    return (
      <section className="stack help-flow" aria-labelledby="help-heading">
        <h1
          id="help-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("page.help.title")}
        </h1>
        <p>{translate("mode.help.unsure.prompt")}</p>
        <p className="muted sme-note">{translate("mode.help.sme_note")}</p>
        <div className="stack">
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => openModules(resolveInfoOnly().modules)}
          >
            <span className="mode-card-label">
              {translate("mode.help.unsure.learn.label")}
            </span>
            <span className="mode-card-blurb">
              {translate("mode.help.unsure.learn.blurb")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={startQuestions}
          >
            <span className="mode-card-label">
              {translate("mode.help.unsure.questions.label")}
            </span>
            <span className="mode-card-blurb">
              {translate("mode.help.unsure.questions.blurb")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => goOptions(resolveInfoOnly())}
          >
            <span className="mode-card-label">
              {translate("mode.help.unsure.options.label")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => goTalk(resolveContactNow().resourceCategories)}
          >
            <span className="mode-card-label">
              {translate("mode.help.unsure.talk.label")}
            </span>
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "questions") {
    const indicatorId = questionOrder[questionIndex];
    const liveRouting = resolveFromAnswers(helpAnswers);
    const talkCategories =
      liveRouting.resourceCategories.length > 0
        ? liveRouting.resourceCategories
        : resolveContactNow().resourceCategories;

    return (
      <section className="stack help-flow" aria-labelledby="help-heading">
        <h1
          id="help-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.help.questions.orientation")}
        </h1>
        <p className="muted">{translate("mode.help.questions.helper")}</p>
        <p className="muted sme-note">{translate("mode.help.sme_note")}</p>

        <div className="question-block">
          <h2 className="question-prompt">{translate(indicatorId)}</h2>
          <div
            className="answer-grid"
            role="group"
            aria-label={translate(indicatorId)}
          >
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "yes")}
            >
              {translate("answer.yes")}
            </button>
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "no")}
            >
              {translate("answer.no")}
            </button>
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "not_sure")}
            >
              {translate("answer.not_sure")}
            </button>
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "prefer_not")}
            >
              {translate("answer.prefer_not")}
            </button>
          </div>
        </div>

        <div className="stack escape-actions">
          <button
            type="button"
            className="text-action"
            onClick={() => goOptions(liveRouting)}
          >
            {translate("answer.see_options")}
          </button>
          <button
            type="button"
            className="text-action"
            onClick={() => goTalk(talkCategories)}
          >
            {translate("answer.talk_to_someone")}
          </button>
          <button type="button" className="text-action" onClick={skipAll}>
            {translate("answer.skip_all")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "modules") {
    const modules =
      activeModules.length > 0 ? activeModules : resolveInfoOnly().modules;
    return (
      <section className="stack help-flow" aria-labelledby="help-heading">
        <h1
          id="help-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.help.modules.heading")}
        </h1>
        <p className="muted sme-note">{translate("mode.help.sme_note")}</p>
        {modules.map((moduleId) => (
          <article key={moduleId} className="info-module">
            <h2>{translate(`${moduleId}.title`)}</h2>
            <p>{translate(`${moduleId}.body`)}</p>
          </article>
        ))}
        <div className="stack">
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={() => goOptions(resolveInfoOnly())}
          >
            {translate("mode.help.modules.continue_options")}
          </button>
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={() => goTalk(resolveInfoOnly().resourceCategories)}
          >
            {translate("answer.find_help")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "options") {
    const framingKey = routing.hasAnyYes
      ? "mode.help.options_framing"
      : "mode.help.options_framing_generic";
    return (
      <section className="stack help-flow" aria-labelledby="help-heading">
        <h1
          id="help-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.help.options.heading")}
        </h1>
        <p>{translate(framingKey)}</p>
        <p className="muted sme-note">{translate("mode.help.sme_note")}</p>
        <div className="stack">
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => openModules(routing.modules)}
          >
            <span className="mode-card-label">
              {translate("mode.help.options.learn")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => goTalk(routing.resourceCategories)}
          >
            <span className="mode-card-label">
              {translate("mode.help.options.talk")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={startQuestions}
          >
            <span className="mode-card-label">
              {translate("mode.help.options.questions")}
            </span>
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  return (
    <section className="stack help-flow" aria-labelledby="help-heading">
      <h1
        id="help-heading"
        className="help-heading"
        ref={headingRef}
        tabIndex={-1}
      >
        {translate("page.help.title")}
      </h1>
      <p className="muted">{translate("mode.help.awareness")}</p>
      <p className="muted">{translate("mode.help.awareness.disclosure")}</p>
      <p className="muted sme-note">{translate("mode.help.sme_note")}</p>

      <h2 className="mode-heading">{translate("mode.help.hub.prompt")}</h2>

      <div className="stack">
        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={startQuestions}
        >
          <span className="mode-card-label">
            {translate("mode.help.hub.understand.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.help.hub.understand.blurb")}
          </span>
        </button>

        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => {
            clearHelpAnswers();
            goOptions(resolveInfoOnly());
          }}
        >
          <span className="mode-card-label">
            {translate("mode.help.hub.options.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.help.hub.options.blurb")}
          </span>
        </button>

        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => goTalk(resolveContactNow().resourceCategories)}
        >
          <span className="mode-card-label">
            {translate("mode.help.hub.talk.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.help.hub.talk.blurb")}
          </span>
        </button>

        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => pushView("unsure")}
        >
          <span className="mode-card-label">
            {translate("mode.help.hub.unsure.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.help.hub.unsure.blurb")}
          </span>
        </button>
      </div>

      <div className="stack escape-actions">
        <button
          type="button"
          className="text-action"
          onClick={() => {
            clearHelpAnswers();
            openModules(resolveUnsafeToAnswer().modules);
          }}
        >
          {translate("mode.help.unsafe_to_answer")}
        </button>
      </div>
      <FlowNav onBack={goBackToLanding} />
    </section>
  );
}
